import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import {
  imageTransformationsToString,
  ProfilePictureMedia,
} from '@renderer-ui-library/molecules'
import dayjs from 'dayjs'
import { SetOptional } from 'type-fest'

export type TSalesManagerPayload = {
  id: string
  attributes: TSalesManager
}

export enum ZohoUserAssignmentPageKey {
  AboutUs = 'About-us',
  Auction = 'Auction',
  BuyingAbroad = 'Buying-Abroad',
  HappyCustomers = 'Happy-Customers',
  Inspection = 'Inspection',
  Payment = 'Payment',
  ProductDetails = 'Product-Details',
  Startpage = 'Startpage',
  Transport = 'Transport',
  Warranty = 'Warranty',
}

export type ZohoUserAssignment = {
  rank: number | null
  pageKey: ZohoUserAssignmentPageKey
}

export type TSalesManager = {
  id: string
  firstName: string
  lastName: string
  email: string
  slackUserId: string
  additionalMentionsSlackUserIds: string | null
  languagesSpoken: string
  isSalesManager: boolean
  isBusinessSpecialist: boolean
  acceptsMessages: boolean
  jobTitle: string
  bioTitle: string
  bioText: string
  enabled: boolean
  countries: TCountry[]
  zohoUserId: string
  assignments?: ZohoUserAssignment[]
}

const hasZohoUserAssignmentFilter =
  (pageKey: ZohoUserAssignmentPageKey) => (salesManager: TSalesManager) =>
    !!salesManager.assignments?.some(
      (assignment) => assignment.pageKey === pageKey
    )

export const getAssignedSalesManagers = (
  salesManagers: TSalesManager[],
  pageKey: ZohoUserAssignmentPageKey
) => {
  return salesManagers
    .filter(hasZohoUserAssignmentFilter(pageKey))
    .sort((a, b) => {
      const aAssignment = a.assignments?.find(
        (assign) => assign.pageKey === pageKey
      )
      const bAssignment = b.assignments?.find(
        (assign) => assign.pageKey === pageKey
      )

      const aRank = aAssignment?.rank
      const bRank = bAssignment?.rank

      if (typeof aRank === 'number' && typeof bRank === 'number') {
        return aRank - bRank
      } else if (typeof aRank === 'number' && bRank == null) {
        return -1
      } else if (aRank == null && typeof bRank === 'number') {
        return 1
      } else if (aRank == null && bRank == null) {
        // If both are null, sort by firstName
        return a.firstName.localeCompare(b.firstName)
      }

      return 0
    })
}
type TCountry = {
  id: string
  locale: TLocale
}

export const getProfilePictureUrl = (
  media: SetOptional<ProfilePictureMedia, 'name' | 'type'>
) => {
  const queryParams = new URLSearchParams({
    updatedAt: dayjs().format('YYYY-MM-DD'),
  })
  const transformations = imageTransformationsToString(
    media.transformations ?? {}
  )
  if (transformations) {
    queryParams.append('tr', transformations)
  }

  return `https://ik.imagekit.io/efarm/user-profile-picture/${media.zohoUserId}/${media.variant}?${queryParams.toString()}`
}

export const getName = (salesManager: TSalesManager) =>
  `${salesManager.firstName} ${salesManager.lastName}`.trim()
